import { z } from 'zod'
import { streamColumnLabel } from './stream-columns'

/**
 * The analytics build's sections (CLI-758 follow-up): everything the build writes beside the
 * articles — the echo chains (`ECHO_SECTION_KEY`), the filter lists (`GENERIC_SECTION_KEYS`) and
 * whatever else a client's build computes (narratives, whitespace, jurisdictions, …), including
 * sections nobody has seen yet. The stream blob carries
 * them unchanged (analytics-dashboard-backend `encode_stream_body`); the reader keeps them with
 * every nested article reduced to a reference (`SectionArticle`) and every list bounded.
 *
 * Headline does not hardcode a renderer per client. `inferSectionDisplay` reads a section's
 * SHAPE — a list of labelled records with figures is a ranking, records with a date and a count
 * a series, records of text a set of cards, `start_art`/`point_art_N`/`sim_N` an echo chain — and
 * says how to show it, recursively, so a section no one has seen before still reads sensibly.
 */

/** An article inside a section, reduced to what identifies it (`toSectionRef` in apps/api's
 *  analytics-cache-sections.ts). */
export const sectionArticleSchema = z.object({
  $article: z.literal(true),
  id: z.string().nullable(),
  headline: z.string().nullable(),
  outlet: z.string().nullable(),
  date: z.string().nullable(),
  url: z.string().nullable(),
  sentiment: z.string().nullable(),
})
export type SectionArticle = z.infer<typeof sectionArticleSchema>

/** The filter lists every build writes; Headline has facets of its own for these. */
export const GENERIC_SECTION_KEYS = ['outlets', 'terms', 'medias', 'headers', 'tags'] as const

/** The section every build writes its echo chains under — the Echoes module's, never a module of
 *  its own. */
export const ECHO_SECTION_KEY = 'analytics'

export const streamSectionsResponseSchema = z.object({
  /** False when the sub-account has no cached payload yet. */
  cached: z.boolean(),
  computedAt: z.string().nullable(),
  /** Every section the build wrote, by its own key, as the reader kept it. */
  sections: z.record(z.string(), z.unknown()),
  /** Paths where the reader cut a list or a text short, or dropped a section over budget. */
  truncated: z.array(z.string()),
})
export type StreamSectionsResponse = z.infer<typeof streamSectionsResponseSchema>

// ── How to show a section ──────────────────────────────────────────────────────────────────────

export type Scalar = string | number | boolean

export interface SectionChild {
  key: string
  label: string
  display: SectionDisplay
}

export interface RankingRow {
  label: string
  values: Record<string, number | null>
  /** The model-written headline of a row named by what it is (a jurisdiction's `label`). */
  subtitle: string | null
  summary: string | null
  /** The row's own nested parts (top outlets, daily counts, articles…), each inferred in turn. */
  details: SectionChild[]
}

export type SectionDisplay =
  | { kind: 'empty' }
  | { kind: 'value'; value: Scalar }
  | { kind: 'facts'; entries: { key: string; label: string; value: Scalar }[] }
  | { kind: 'list'; items: string[] }
  | { kind: 'articles'; items: SectionArticle[] }
  | { kind: 'chains'; chains: { article: SectionArticle; similarity: number | null }[][] }
  | {
      kind: 'ranking'
      /** The field the rows are named by ('' for an object of label → figure). */
      labelKey: string
      /** The figures, most telling first — the first is what the bars draw. */
      valueKeys: string[]
      rows: RankingRow[]
    }
  | {
      kind: 'series'
      dateKey: string
      valueKey: string
      points: { date: string; value: number }[]
    }
  | {
      kind: 'cards'
      cards: {
        title: string
        subtitle: string | null
        summary: string | null
        details: SectionChild[]
      }[]
    }
  | { kind: 'group'; children: SectionChild[] }
  | { kind: 'raw'; value: unknown }

/** Field names a record is most likely named by, in order: what it IS (a jurisdiction, a
 *  competitor, a theme) before the headline a model wrote about it (`label`), which is then the
 *  row's subtitle. */
const LABEL_KEYS = [
  'jurisdiction',
  'competitor',
  'l1',
  'voice_tag',
  'domain',
  'company',
  'name',
  'narrative',
  'keyword',
  'l2',
  'label',
  'title',
  'category',
]
const SUBTITLE_KEYS = ['label', 'title']
/** Figures most likely to be the point of a ranking, in order. */
const PRIMARY_VALUES = [
  'volume',
  'count',
  'article_count',
  'share',
  'total_reach',
  'reach',
  'recent_volume',
  'growth_pct',
]
const SUMMARY_KEYS = ['summary', 'description', 'narrative_summary']
const MAX_DEPTH = 5
const MAX_VALUES = 6

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const isScalar = (value: unknown): value is Scalar =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)
const DATE_LIKE = /^\d{4}-\d{2}-\d{2}/

export function isSectionArticle(value: unknown): value is SectionArticle {
  return isRecord(value) && value.$article === true
}

const isEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isRecord(value) && Object.keys(value).length === 0)

/** `start_art`, `point_art_1`, `sim_1`, … — the build's echo chain. */
function chainOf(record: Record<string, unknown>) {
  const start = record.start_art
  if (!isSectionArticle(start)) return null
  const steps: { article: SectionArticle; similarity: number | null }[] = [
    { article: start, similarity: null },
  ]
  for (let at = 1; ; at++) {
    const point = record[`point_art_${at}`]
    if (!isSectionArticle(point)) break
    const sim = record[`sim_${at}`]
    steps.push({ article: point, similarity: isNumber(sim) ? sim : null })
  }
  return steps
}

/** The field a set of records is named by: a known name first, else the string field with the
 *  most distinct values, carried by at least four in five of the records. */
function labelKeyOf(records: Record<string, unknown>[]): string | null {
  const carried = (key: string) =>
    records.filter((record) => typeof record[key] === 'string' && record[key] !== '').length >=
    records.length * 0.8
  const known = LABEL_KEYS.find(carried)
  if (known) return known
  const keys = [...new Set(records.flatMap((record) => Object.keys(record)))].filter(
    (key) => carried(key) && !SUMMARY_KEYS.includes(key),
  )
  let best: string | null = null
  let bestDistinct = 0
  for (const key of keys) {
    const distinct = new Set(records.map((record) => record[key])).size
    if (distinct > bestDistinct) [best, bestDistinct] = [key, distinct]
  }
  return best
}

function childrenOf(
  record: Record<string, unknown>,
  skip: Set<string>,
  depth: number,
): SectionChild[] {
  const facts: { key: string; label: string; value: Scalar }[] = []
  const children: SectionChild[] = []
  for (const [key, value] of Object.entries(record)) {
    if (skip.has(key) || isEmpty(value)) continue
    if (isScalar(value)) facts.push({ key, label: streamColumnLabel(key), value })
    else {
      children.push({
        key,
        label: streamColumnLabel(key),
        display: inferSectionDisplay(value, depth + 1),
      })
    }
  }
  return facts.length > 0
    ? [{ key: '', label: 'Details', display: { kind: 'facts', entries: facts } }, ...children]
    : children
}

function recordsDisplay(records: Record<string, unknown>[], depth: number): SectionDisplay {
  const chains = records.map(chainOf)
  if (chains.every((chain) => chain !== null)) {
    return { kind: 'chains', chains: chains as NonNullable<(typeof chains)[number]>[] }
  }
  const keys = [...new Set(records.flatMap((record) => Object.keys(record)))]
  const numericKeys = keys.filter(
    (key) => records.filter((record) => isNumber(record[key])).length >= records.length * 0.5,
  )
  // A date and a figure on every record: a series over time (daily counts).
  const dateKey = keys.find((key) =>
    records.every((record) => typeof record[key] === 'string' && DATE_LIKE.test(record[key])),
  )
  if (dateKey && numericKeys.length > 0 && keys.length <= numericKeys.length + 1) {
    const valueKey = numericKeys[0]!
    return {
      kind: 'series',
      dateKey,
      valueKey,
      points: records.map((record) => ({
        date: String(record[dateKey]),
        value: isNumber(record[valueKey]) ? record[valueKey] : 0,
      })),
    }
  }
  const labelKey = labelKeyOf(records)
  const summaryKey = SUMMARY_KEYS.find((key) =>
    records.some((record) => typeof record[key] === 'string'),
  )
  const valueKeys = numericKeys
    .filter((key) => key !== labelKey && key !== 'id')
    .sort((a, b) => {
      const rank = (key: string) => {
        const at = PRIMARY_VALUES.indexOf(key)
        return at === -1 ? PRIMARY_VALUES.length : at
      }
      return rank(a) - rank(b)
    })
    .slice(0, MAX_VALUES)
  // The headline a model wrote about the row, when the row is named by what it is.
  const subtitleKey = SUBTITLE_KEYS.find(
    (key) => key !== labelKey && records.some((record) => typeof record[key] === 'string'),
  )
  // An `id` names the row for the build, not for a reader: never a part of its own.
  const skip = new Set(['id', labelKey ?? '', summaryKey ?? '', subtitleKey ?? '', ...valueKeys])
  const named = (record: Record<string, unknown>, at: number) =>
    labelKey && typeof record[labelKey] === 'string' ? record[labelKey] : `#${at + 1}`
  const summaryOf = (record: Record<string, unknown>) =>
    summaryKey && typeof record[summaryKey] === 'string' ? record[summaryKey] : null
  const subtitleOf = (record: Record<string, unknown>) =>
    subtitleKey && typeof record[subtitleKey] === 'string' ? record[subtitleKey] : null
  if (valueKeys.length > 0) {
    return {
      kind: 'ranking',
      labelKey: labelKey ?? '',
      valueKeys,
      rows: records.map((record, at) => ({
        label: named(record, at),
        values: Object.fromEntries(
          valueKeys.map((key) => [key, isNumber(record[key]) ? record[key] : null]),
        ),
        subtitle: subtitleOf(record),
        summary: summaryOf(record),
        details: childrenOf(record, skip, depth),
      })),
    }
  }
  return {
    kind: 'cards',
    cards: records.map((record, at) => ({
      title: named(record, at),
      subtitle: subtitleOf(record),
      summary: summaryOf(record),
      details: childrenOf(record, skip, depth),
    })),
  }
}

/**
 * How to show a section, judged from its shape alone. Recursive: a ranking row's nested lists
 * (top outlets, daily counts, top articles) and a group's parts are inferred the same way, down
 * to a depth past which the rest is shown as it is.
 */
export function inferSectionDisplay(value: unknown, depth = 0): SectionDisplay {
  if (isEmpty(value)) return { kind: 'empty' }
  if (isScalar(value)) return { kind: 'value', value }
  if (depth > MAX_DEPTH) return { kind: 'raw', value }
  if (isSectionArticle(value)) return { kind: 'articles', items: [value] }
  if (Array.isArray(value)) return arrayDisplay(value, depth)
  if (!isRecord(value)) return { kind: 'raw', value }
  return recordDisplay(value, depth)
}

function arrayDisplay(value: unknown[], depth: number): SectionDisplay {
  const items = value.filter((item) => !isEmpty(item))
  if (items.length === 0) return { kind: 'empty' }
  if (items.every(isScalar)) return { kind: 'list', items: items.map(String) }
  if (items.every(isSectionArticle)) return { kind: 'articles', items }
  if (items.every(isRecord)) return recordsDisplay(items, depth)
  return { kind: 'raw', value }
}

/** One record: a chain, a ranking of label → figure, a record's facts, or a group of parts. */
function recordDisplay(value: Record<string, unknown>, depth: number): SectionDisplay {
  const chain = chainOf(value)
  if (chain) return { kind: 'chains', chains: [chain] }
  const entries = Object.entries(value).filter(([, item]) => !isEmpty(item))
  // label → figure (jurisdiction counts, a sentiment breakdown): a ranking of its own — unless
  // the keys are field names (`recent_days`, `prior_days`), which makes it a record's facts.
  const fieldNamed =
    entries.some(([key]) => key.includes('_')) &&
    entries.every(([key]) => /^[a-z][a-z0-9_]*$/.test(key))
  if (entries.length > 0 && !fieldNamed && entries.every(([, item]) => isNumber(item))) {
    return {
      kind: 'ranking',
      labelKey: '',
      valueKeys: ['value'],
      rows: entries.map(([label, item]) => ({
        label,
        values: { value: item as number },
        subtitle: null,
        summary: null,
        details: [],
      })),
    }
  }
  if (entries.every(([, item]) => isScalar(item))) {
    return {
      kind: 'facts',
      entries: entries.map(([key, item]) => ({
        key,
        label: streamColumnLabel(key),
        value: item as Scalar,
      })),
    }
  }
  return { kind: 'group', children: childrenOf(value, new Set(), depth) }
}
